import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for detecting barcode scanner input
 * @param {Function} onScan - Callback function when barcode is scanned (receives the scanned value string)
 * @returns {Object} - { isActive, startScanning, stopScanning }
 */
export const useBarcodeScanner = (onScan) => {
    const [isActive, setIsActive] = useState(false);
    const buffer = useRef('');
    const lastKeyTime = useRef(null);
    const clearBufferTimeout = useRef(null);

    // Barcode scanners typically input characters with < 50ms between keystrokes
    // Human typing is usually > 100ms between keystrokes
    const SCANNER_THRESHOLD_MS = 75;
    const BUFFER_CLEAR_MS = 100;

    const startScanning = useCallback(() => {
        setIsActive(true);
    }, []);

    const stopScanning = useCallback(() => {
        setIsActive(false);
        buffer.current = '';
        lastKeyTime.current = null;
        if (clearBufferTimeout.current) {
            clearTimeout(clearBufferTimeout.current);
        }
    }, []);

    useEffect(() => {
        if (!isActive) return;

        const handleKeyPress = (e) => {
            const currentTime = Date.now();
            const timeSinceLastKey = lastKeyTime.current ? currentTime - lastKeyTime.current : null;

            // Clear any existing buffer timeout
            if (clearBufferTimeout.current) {
                clearTimeout(clearBufferTimeout.current);
            }

            if (e.key === 'Enter') {
                // Only process Enter as scanner input if we have rapid-fire characters in buffer
                if (buffer.current && timeSinceLastKey !== null && timeSinceLastKey < SCANNER_THRESHOLD_MS) {
                    e.preventDefault();

                    if (onScan) {
                        onScan(buffer.current);
                    }

                    buffer.current = '';
                    lastKeyTime.current = null;
                }
                return;
            }

            // Ignore special keys (except printable characters)
            if (e.key.length > 1) {
                return;
            }

            // Check if this keystroke is rapid enough to be from scanner
            if (timeSinceLastKey < SCANNER_THRESHOLD_MS || timeSinceLastKey === null) {
                // This is likely scanner input - prevent default and buffer it
                e.preventDefault();
                buffer.current += e.key;
            } else {
                // This is likely human typing - let it through
                // Clear the buffer since this is a new typing session
                buffer.current = '';
            }

            lastKeyTime.current = currentTime;

            // Set a timeout to clear the buffer if no more keys come
            clearBufferTimeout.current = setTimeout(() => {
                buffer.current = '';
                lastKeyTime.current = null;
            }, BUFFER_CLEAR_MS);
        };

        window.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
            if (clearBufferTimeout.current) {
                clearTimeout(clearBufferTimeout.current);
            }
        };
    }, [isActive, onScan]);

    return {
        isActive,
        startScanning,
        stopScanning,
    };
};
